/* eslint-disable @typescript-eslint/naming-convention */
import React, { ReactNode, useCallback, useContext, useRef, useState, useEffect } from 'react';
import { ListItem, ListItemProps, SkeletonLine, Toggle } from '@lesmills-international/components';
import { Elements, HTMLSerializer, RichText } from 'prismic-reactjs';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import {
  Button,
  EmailPreferenceDescription,
  EmailPreferenceInfo,
  SectionTitle,
} from './styles';
import { userContext } from '../../../../gatsby-theme-engagement/src/context';
import useUpdateSubcomms from '../../hooks/mutation/useUpdateSubcomms';
import useUpdateSubCommsV2 from '../../hooks/mutation/useUpdateSubCommsV2';
import useSendDOIConfirmEmail from '../../hooks/mutation/useSendDOIConfirmEmail';
import useUpdateUsageAnalyticsPreference from '../../hooks/mutation/useUpdateUsageAnalyticsPreference';
import useListUserPreferences from '../../hooks/queries/useListUserPreferences';
import { addErrorInDatadogRum } from '../../utils/utilities';
import {
  PREFERENCE_TYPE_EMAIL_MARKETING,
  PREFERENCE_TYPE_DATA_PRIVACY,
  TOGGLE_SKELETON_WIDTH,
  TOGGLE_SKELETON_HEIGHT,
  PREFERENCE_REFRESH_DELAY_MS,
  EMAIL_CHANGE_RECONCILE_TIMEOUT_MS,
  LAST_EMAIL_STORAGE_PREFIX,
} from './constants';
import { getPreferenceByType } from './consentPreferencesUtils';
import useDebouncedConsentUpdate from './hooks/useDebouncedConsentUpdate';
import useConsentPreferencesCache from './hooks/useConsentPreferencesCache';

interface ToggleSkeletonProps {
  testId: string;
}

const ToggleSkeleton = ({ testId }: ToggleSkeletonProps) => (
  <span
    data-testid={testId}
    style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '20px' }}
  >
    <SkeletonLine width={TOGGLE_SKELETON_WIDTH} height={TOGGLE_SKELETON_HEIGHT} />
  </span>
);

interface EmailPreferencesLocationState {
  fromHeader?: boolean;
}

interface Props {
  data: Pick<
    Queries.PrismicAccountOverviewPageData,
    | 'email_preferences_section_title'
    | 'email_preferences_title'
    | 'email_preferences_description'
    | 'email_preferences_error_message'
    | 'double_opt_in_message'
    | 'email_sent_message'
    | 'usage_analytics_title'
    | 'usage_analytics_description'
    | 'usage_analytics_error_message'
  >;
  locationState?: EmailPreferencesLocationState | null;
}

const EmailPreferencesSection = ({ data, locationState }: Props) => {
  const { subComms, subCommsConfirmed, refetchUser, userId, email, addressCountry } =
    useContext(userContext);
  const { getCached, updateCache, clearPending } = useConsentPreferencesCache(userId);
  const [isCacheSeeded, setIsCacheSeeded] = useState(false);
  const cacheSeededRef = useRef(false);

  const { rtUpdateSubCommsMutationNew, rtDataConsentCheckbox, rtDoubleOptIn } = useFlags();

  const {
    data: preferencesData,
    loading: preferencesLoading,
    refetch: refetchPreferences,
  } = useListUserPreferences({
    skip: !rtDataConsentCheckbox,
  });
  const preferences = preferencesData?.listUserPreferences;
  const emailPreference = getPreferenceByType(preferences, PREFERENCE_TYPE_EMAIL_MARKETING);
  const usageAnalyticsPreference = getPreferenceByType(preferences, PREFERENCE_TYPE_DATA_PRIVACY);

  // isProcessing tracks the subcomms mutation in-flight — used only for emailNotConfirmed logic
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const emailSentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const EMAIL_SENT_BANNER_DURATION_MS = 5000;

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Disable the toggle while a post is in flight (no overlapping mutations). LPB-1570.
  const [isPosting, setIsPosting] = useState(false);

  // Optimistic re-grey after a DE email change (which re-arms marketing as pending),
  // until the server catches up or a safety timeout. LPB-1570.
  const [emailChangeReconciling, setEmailChangeReconciling] = useState(false);
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Optimistic awaiting-grey after a DE opt-in, until listUserPreferences confirms
  // pending (or a safety timeout) — stops the toggle blinking back ON in the gap. LPB-1570.
  const [optInReconciling, setOptInReconciling] = useState(false);
  const optInTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Local state for toggles — set on click, seeded from server on load, never cleared back to undefined
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [pltvAnalyticsAgreed, setPltvAnalyticsAgreed] = useState(false);

  const [handleUpdateUsageAnalytics, { error: usageAnalyticsError }] =
    useUpdateUsageAnalyticsPreference();

  const [handleUpdateSubcomms, { error }] = useUpdateSubcomms();

  const [handleUpdateSubCommsV2, { error: errorV2 }] = useUpdateSubCommsV2();

  const [handleResendEmail, { error: resendError }] = useSendDOIConfirmEmail();

  // DE double opt-in: both flags + a German account (scopes the optimistic re-grey). LPB-1570.
  const isGermanDoi =
    rtDoubleOptIn && rtDataConsentCheckbox && addressCountry?.toLowerCase() === 'de';

  // DE double opt-in (LPB-1506): when on, "awaiting confirmation" comes from the
  // resolver's `confirmed: false`; when off, fall back to the legacy CDS signal.
  const emailAwaitingConfirmation = rtDoubleOptIn
    ? Boolean(emailPreference?.enabled && emailPreference?.confirmed === false)
    : Boolean(subComms && !subCommsConfirmed);

  // Toggle stays ON but greyed while awaiting confirmation (not on emailSent — a
  // resend isn't a confirmation); OR in the optimistic re-grey. LPB-1506 / LPB-1570.
  const emailNotConfirmed =
    (emailAwaitingConfirmation || emailChangeReconciling || optInReconciling) && !isProcessing;

  const emailChecked = emailEnabled;

  // Wait a few seconds after a post, then refetch listUserPreferences once. The
  // awaiting-confirmation UI is server-derived, so this reflects the change
  // without a manual refresh. Guarded on the checkbox FF so a skipped query is
  // never force-refetched; a newer post resets the timer. LPB-1570.
  const scheduleBackgroundRefresh = useCallback(() => {
    if (!rtDataConsentCheckbox || !refetchPreferences) return;
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      refetchPreferences().catch(() => undefined);
    }, PREFERENCE_REFRESH_DELAY_MS);
  }, [rtDataConsentCheckbox, refetchPreferences]);

  const performEmailUpdate = useCallback(
    async (checked: boolean, successCallback?: () => void) => {
      if (rtDataConsentCheckbox) {
        // New path: call userPreferences mutation directly
        if (!userId || !email) return;
        // Note: updateSubCommsV2 is intentionally NOT called here.
        // When the preference centre flag is active, handleUpdateUsageAnalytics
        // is the source of truth. Calling updateSubCommsV2 concurrently triggers
        // a Didomi update that races against the preference centre write and can
        // negate the user's choice. See LPB-1425.
        setIsPosting(true); // disable the toggle while the post is in flight. LPB-1570.
        // DE opt-in: optimistically grey + show the awaiting message for the whole
        // post→refetch window so the toggle never blinks back ON; the hand-off effect
        // clears it once the server reports pending (or the safety timeout fires). LPB-1570.
        if (isGermanDoi && checked) {
          setOptInReconciling(true);
          if (optInTimerRef.current) clearTimeout(optInTimerRef.current);
          optInTimerRef.current = setTimeout(
            () => setOptInReconciling(false),
            EMAIL_CHANGE_RECONCILE_TIMEOUT_MS,
          );
        } else if (!checked) {
          setOptInReconciling(false);
        }
        try {
          await handleUpdateUsageAnalytics({
            variables: {
              input: {
                user_id: userId,
                user_email: email.toLowerCase(),
                preferences: {
                  marketing_subscribed: checked,
                  // Send the user's CURRENT pLTV toggle state, not the server
                  // value. Preferring usageAnalyticsPreference.enabled here
                  // clobbered an optimistic data-privacy change back to the
                  // stale server value whenever the user toggled marketing,
                  // which left the data-privacy toggle showing the wrong state.
                  // LPB-1506 / LPB-1421.
                  pltv_analytics_agreed: pltvAnalyticsAgreed,
                },
              },
            },
          });
          successCallback?.();
          scheduleBackgroundRefresh();
          // Don't clearPending here — wait for listUserPreferences to confirm
          // the API value matches. Clearing eagerly causes the toggle to snap
          // back when the API returns a stale value before Didomi propagates.
        } catch (err: unknown) {
          addErrorInDatadogRum(err as Error);
          setEmailEnabled(!checked); // revert on error
          updateCache(PREFERENCE_TYPE_EMAIL_MARKETING, !checked, false); // revert cache
          setOptInReconciling(false); // drop the optimistic grey — the opt-in failed
        } finally {
          setIsPosting(false);
        }
        return;
      }

      // Old path: updateSubComms mutation
      setIsProcessing(true);
      const onCompleted = () => {
        setIsProcessing(false);
        successCallback?.();
      };
      const onError = (err: Error) => {
        addErrorInDatadogRum(err);
        setIsProcessing(false);
        setEmailEnabled(!checked); // revert on error
      };
      if (rtUpdateSubCommsMutationNew) {
        handleUpdateSubCommsV2({ variables: { value: checked }, onCompleted, onError });
      } else {
        handleUpdateSubcomms({
          variables: { input: { subComms: checked } },
          onCompleted,
          onError,
        });
      }
    },
    [
      rtDataConsentCheckbox,
      userId,
      email,
      pltvAnalyticsAgreed,
      isGermanDoi,
      rtUpdateSubCommsMutationNew,
      handleUpdateSubCommsV2,
      handleUpdateSubcomms,
      handleUpdateUsageAnalytics,
      updateCache,
      scheduleBackgroundRefresh,
    ],
  );

  const handleOnChange = useDebouncedConsentUpdate<[boolean, (() => void)?]>(
    (checked, successCallback) => performEmailUpdate(checked, successCallback),
  );

  const handleResendConfirmation = () => {
    if (emailSentTimerRef.current) clearTimeout(emailSentTimerRef.current);
    handleResendEmail({
      fetchPolicy: 'no-cache'
    })
      .then(() => {
        setEmailSent(true);
        emailSentTimerRef.current = setTimeout(() => setEmailSent(false), EMAIL_SENT_BANNER_DURATION_MS);
      })
      .catch(() => undefined); // surfaced via resendError; avoid an unhandled rejection here
  };

  // Clear pending timers on unmount (avoid refetch/setState after unmount).
  useEffect(
    () => () => {
      if (emailSentTimerRef.current) clearTimeout(emailSentTimerRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      if (optInTimerRef.current) clearTimeout(optInTimerRef.current);
    },
    [],
  );

  // Background-refresh once after page load to pick up state that settled after
  // the initial query (e.g. a confirmation completed elsewhere). LPB-1570.
  useEffect(() => {
    scheduleBackgroundRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On a detected email change, optimistically re-grey for a subscribed DE/DOI user
  // (the change re-arms marketing as pending) to close the window before the refetch
  // lands; the clear effect below hands back to the server signal. LPB-1570 / LPB-1573.
  useEffect(() => {
    if (typeof window === 'undefined' || !userId || !email) return;
    // Wait for the preference to load before consuming the change marker, else it
    // advances before the grey decision and the change signal is lost. LPB-1573.
    if (isGermanDoi && emailPreference?.enabled === undefined) return;

    const key = `${LAST_EMAIL_STORAGE_PREFIX}${userId}`;
    const current = email.toLowerCase();
    let lastEmail: string | null = null;
    try {
      lastEmail = localStorage.getItem(key);
      // Record unconditionally so the comparison is self-idempotent (greys once per change).
      localStorage.setItem(key, current);
    } catch {
      return; // no localStorage — server-derived state still reconciles, just not instantly
    }

    if (!lastEmail || lastEmail === current) return; // first visit or unchanged
    // Gate on Didomi-derived enabled, not stale CDS subComms (LPB-1425). LPB-1573.
    if (!isGermanDoi || emailPreference?.enabled !== true) return;

    setEmailChangeReconciling(true);
    refetchPreferences?.().catch(() => undefined); // pull the re-armed pending state ASAP
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => {
      // Safety net: force a final read and apply it so the toggle never ends on stale
      // state; clear only once that read resolves. LPB-1570.
      const handOff = () => setEmailChangeReconciling(false);
      if (refetchPreferences) {
        refetchPreferences()
          .catch(() => undefined)
          .finally(handOff);
      } else {
        handOff();
      }
    }, EMAIL_CHANGE_RECONCILE_TIMEOUT_MS);
  }, [userId, email, isGermanDoi, emailPreference?.enabled, refetchPreferences]);

  // Hand off to the server signal only once it actually shows pending
  // (emailAwaitingConfirmation), so the grey never blinks off in between. LPB-1570.
  useEffect(() => {
    if (!emailAwaitingConfirmation) return;
    if (emailChangeReconciling) {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      setEmailChangeReconciling(false);
    }
    if (optInReconciling) {
      if (optInTimerRef.current) clearTimeout(optInTimerRef.current);
      setOptInReconciling(false);
    }
  }, [emailChangeReconciling, optInReconciling, emailAwaitingConfirmation]);

  const {
    email_preferences_section_title,
    email_preferences_title,
    email_preferences_description,
    email_preferences_error_message,
    email_sent_message,
    double_opt_in_message,
    usage_analytics_title,
    usage_analytics_description,
    usage_analytics_error_message,
  } = data;

  const performUsageAnalyticsUpdate = useCallback(
    async (checked: boolean) => {
      if (!userId || !email) return;
      try {
        await handleUpdateUsageAnalytics({
          variables: {
            input: {
              user_id: userId,
              user_email: email.toLowerCase(),
              preferences: {
                marketing_subscribed: emailEnabled,
                pltv_analytics_agreed: checked,
              },
            },
          },
        });
        scheduleBackgroundRefresh();
        // Don't clearPending here — wait for listUserPreferences to confirm
        // the API value matches. Clearing eagerly causes the toggle to snap
        // back when the API returns a stale value before Didomi propagates.
      } catch (err: unknown) {
        addErrorInDatadogRum(err as Error);
        setPltvAnalyticsAgreed(!checked); // revert optimistic on error
        updateCache(PREFERENCE_TYPE_DATA_PRIVACY, !checked, false); // revert cache
      }
    },
    [userId, email, emailEnabled, handleUpdateUsageAnalytics, updateCache, scheduleBackgroundRefresh],
  );

  const handleUsageAnalyticsChange = useDebouncedConsentUpdate<[boolean]>(
    (checked) => performUsageAnalyticsUpdate(checked),
  );

  const convertPrismicElementToButton: HTMLSerializer<ReactNode> = (
    type: string,
    _: unknown,
    content: ReactNode,
  ) => {
    if (type === Elements.strong) {
      return <Button onClick={handleResendConfirmation}>{content}</Button>;
    }
  };

  const usageAnalyticsItem: ListItemProps[0] = {
    title: usage_analytics_title,
    description: (
      <EmailPreferenceDescription>
        {usageAnalyticsError && (
          <div className="error" role="alert">
            {usage_analytics_error_message}
          </div>
        )}
        {usage_analytics_description}
        {rtDataConsentCheckbox && preferencesLoading && !isCacheSeeded ? (
          <ToggleSkeleton testId="toggle-skeleton-analytics" />
        ) : (
          <Toggle
            checked={pltvAnalyticsAgreed}
            onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setPltvAnalyticsAgreed(checked);
              updateCache(PREFERENCE_TYPE_DATA_PRIVACY, checked, true);
              handleUsageAnalyticsChange(checked);
            }}
            sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '20px' }}
          />
        )}
      </EmailPreferenceDescription>
    ),
  };

  const items: ListItemProps = [
    {
      title: email_preferences_title,
      description: (
        <EmailPreferenceDescription>
          {/* Prompt stays mounted the whole time we're awaiting — it holds the
              only working resend control (convertPrismicElementToButton). LPB-1506. */}
          {emailNotConfirmed && double_opt_in_message && (
            <EmailPreferenceInfo>
              <RichText
                render={double_opt_in_message.richText}
                key={double_opt_in_message.text}
                htmlSerializer={convertPrismicElementToButton}
              />
            </EmailPreferenceInfo>
          )}

          {(error || errorV2 || resendError) && (
            <div className="error" role="alert">
              {email_preferences_error_message}
            </div>
          )}
          {/* "Email sent" resend acknowledgement. Not gated on
              !double_opt_in_message — the resend control lives inside that
              prompt, so gating there would suppress the banner. LPB-1570. */}
          {emailSent && (
            <div className="success" role="alert">
              {email_sent_message}
            </div>
          )}
          {email_preferences_description}
          {rtDataConsentCheckbox && preferencesLoading && !isCacheSeeded ? (
            <ToggleSkeleton testId="toggle-skeleton-email" />
          ) : (
            <Toggle
              checked={emailChecked}
              onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                setEmailEnabled(checked);
                updateCache(PREFERENCE_TYPE_EMAIL_MARKETING, checked, true);
                handleOnChange(checked);
              }}
              disabled={!!emailNotConfirmed || isPosting}
              sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '20px' }}
            />
          )}
        </EmailPreferenceDescription>
      ),
    },
    ...(rtDataConsentCheckbox && usage_analytics_title && usage_analytics_description
      ? [usageAnalyticsItem]
      : []),
  ];

  useEffect(() => {
    if (locationState?.fromHeader) {
      refetchUser?.();
    }
  }, [locationState, refetchUser]);

  // Seed toggle state from cache on mount — skips skeleton for returning users
  useEffect(() => {
    if (!rtDataConsentCheckbox || cacheSeededRef.current) return;
    const cached = getCached();
    if (!cached) return;
    cacheSeededRef.current = true;
    if (cached[PREFERENCE_TYPE_EMAIL_MARKETING]) {
      setEmailEnabled(cached[PREFERENCE_TYPE_EMAIL_MARKETING].value);
    }
    if (cached[PREFERENCE_TYPE_DATA_PRIVACY]) {
      setPltvAnalyticsAgreed(cached[PREFERENCE_TYPE_DATA_PRIVACY].value);
    }
    setIsCacheSeeded(true);
  }, [rtDataConsentCheckbox, getCached]);

  useEffect(() => {
    if (!rtDataConsentCheckbox) {
      if (subComms !== undefined) setEmailEnabled(subComms);
      return;
    }
    if (emailPreference === undefined) return;
    const emailCache = getCached()?.[PREFERENCE_TYPE_EMAIL_MARKETING];
    if (emailCache?.pendingSync) {
      // User has a pending change — check if API now matches
      if (emailCache.value === emailPreference.enabled) {
        // API caught up — safe to clear pending and trust API
        clearPending(PREFERENCE_TYPE_EMAIL_MARKETING);
      }
      // Either way, keep showing localStorage value (don't overwrite toggle)
      return;
    }
    // No pending sync — trust API, update cache
    setEmailEnabled(emailPreference.enabled);
    updateCache(PREFERENCE_TYPE_EMAIL_MARKETING, emailPreference.enabled, false);
  }, [rtDataConsentCheckbox, emailPreference, subComms, getCached, updateCache, clearPending]);

  // Re-fire email mutation if API responded but cache still has pendingSync: true
  useEffect(() => {
    if (!rtDataConsentCheckbox || !emailPreference || !userId || !email) return;
    const cached = getCached();
    const emailCache = cached?.[PREFERENCE_TYPE_EMAIL_MARKETING];
    if (!emailCache?.pendingSync) return;

    const syncEmail = async () => {
      try {
        handleUpdateSubCommsV2({ variables: { value: emailCache.value } });
        await handleUpdateUsageAnalytics({
          variables: {
            input: {
              user_id: userId,
              user_email: email.toLowerCase(),
              preferences: {
                marketing_subscribed: emailCache.value,
                pltv_analytics_agreed:
                  cached?.[PREFERENCE_TYPE_DATA_PRIVACY]?.value ?? pltvAnalyticsAgreed,
              },
            },
          },
        });
        // Don't clearPending here — the sync useEffect above will clear it
        // once listUserPreferences returns a matching value.
      } catch {
        // leave pendingSync: true — will retry on next page load
      }
    };
    syncEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailPreference]); // intentionally fires only when API first responds

  useEffect(() => {
    if (!rtDataConsentCheckbox || usageAnalyticsPreference === undefined) return;
    const analyticsCache = getCached()?.[PREFERENCE_TYPE_DATA_PRIVACY];
    if (analyticsCache?.pendingSync) {
      // User has a pending change — check if API now matches
      if (analyticsCache.value === usageAnalyticsPreference.enabled) {
        // API caught up — safe to clear pending
        clearPending(PREFERENCE_TYPE_DATA_PRIVACY);
      }
      // Either way, keep showing localStorage value (don't overwrite toggle)
      return;
    }
    // No pending sync — trust API, update cache
    setPltvAnalyticsAgreed(usageAnalyticsPreference.enabled);
    updateCache(PREFERENCE_TYPE_DATA_PRIVACY, usageAnalyticsPreference.enabled, false);
  }, [rtDataConsentCheckbox, usageAnalyticsPreference, getCached, updateCache, clearPending]);

  // Re-fire analytics mutation if API responded but cache still has pendingSync: true
  useEffect(() => {
    if (!rtDataConsentCheckbox || !usageAnalyticsPreference || !userId || !email) return;
    const cached = getCached();
    const analyticsCache = cached?.[PREFERENCE_TYPE_DATA_PRIVACY];
    if (!analyticsCache?.pendingSync) return;

    const syncAnalytics = async () => {
      try {
        await handleUpdateUsageAnalytics({
          variables: {
            input: {
              user_id: userId,
              user_email: email.toLowerCase(),
              preferences: {
                marketing_subscribed:
                  cached?.[PREFERENCE_TYPE_EMAIL_MARKETING]?.value ?? emailEnabled,
                pltv_analytics_agreed: analyticsCache.value,
              },
            },
          },
        });
        // Don't clearPending here — the sync useEffect above will clear it
        // once listUserPreferences returns a matching value.
      } catch {
        // leave pendingSync: true — will retry on next page load
      }
    };
    syncAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageAnalyticsPreference]); // intentionally fires only when API first responds

  return (
    <div>
      <SectionTitle>{email_preferences_section_title}</SectionTitle>
      <ListItem items={items} />
    </div>
  );
};

export default EmailPreferencesSection;
